import React, { useEffect, useState } from 'react';
import type { HTMLAttributes } from 'react';
import type { AllColorType, ThemeType } from '../../../util/types';
import type { ThemeProps } from 'styled-components';
import classNames from 'classnames';
import { getPolishedColors } from '../../../util/styled';
import styled, { css } from 'styled-components';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  label?: string | null;
  value?: number | string | null;
  unit?: string | null;
  description?: string | null;
  highlight?: boolean;
  type?: 'average' | 'highlight';
  overrideColor?: AllColorType | null;
  margin?: boolean;
  show?: boolean;
  children?: React.ReactNode;
}

const HighlightCard = ({ label, value, unit, description, highlight = false, margin = true, type = 'highlight', overrideColor = null, show = true, children, ...other }: Props): JSX.Element => {
  const [ values, setValues ] = useState({ label, value, unit, description });

  useEffect(() => {
    if (show) {
      setValues({ label, value, unit, description });
    }
  }, [ show, label, value, unit, description ]);

  const classStyle = type === 'highlight' ? `flex items-center font-bold italic rounded-default px-2 py-1 bg-white border-3 text-primary ${classNames({ 'border-primary': highlight, 'border-secondary': !highlight, 'ml-md': margin })} ` : 'flex items-center bg-header border-header border-3 rounded-default px-2 py-1 ml-md';

  return (
    <div className={`flex items-center transition-opacity duration-700 ${show ? 'opacity-100' : 'opacity-0 absolute pointer-events-none'}`}>
      <div {...other as HTMLProps}>
        <div className={'flex items-center'}>
          <Wrapper
            className={classStyle}
            $color={overrideColor}
            data-cy={highlight ? 'highlight-label' : 'label'}
          >
            {values.label}
            {null != values.value && (
              <span
                className={`uppercase ${classNames({ 'ml-sm': null != values.label })}`}
                data-cy={highlight ? 'highlight-value' : 'value'}
              >
                {values.value}
              </span>
            )}
            {null != values.unit && (
              <span className={'text-sm ml-sm'}>
                {values.unit}
              </span>
            )}
          </Wrapper>
          { values.description && <div className={'ml-sm text-sm'}>{values.description}</div> }
        </div>
      </div>
      {children}
    </div>
  );
};

const getColor = (props: ThemeProps<ThemeType> & { $color: Props['overrideColor'] }) => {

  if (props.$color) {
    const colors = getPolishedColors({ theme: props.theme, $color: props.$color });

    return css`
      background: ${colors.main.color};
      color: ${colors.main.readable};
    `;
  }
};

const Wrapper = styled('div')<{ $color: Props['overrideColor'] }>`
  ${(props) => getColor(props)}
`;

export default HighlightCard;
